<template>
  <ScrollIntersectionObserver>
    <slot name="subjectImage" />
    <HintText data-test-id="ChoiceMultiple_HintText">
      {{
        maxAnswers === Infinity
          ? c('multiple_choice.select_answers')
          : c('multiple_choice.answer_limit', {
              quantity: maxAnswers,
              placeholders: { numAnswers: `${maxAnswers}` },
            })
      }}
    </HintText>
    <ChoiceFields
      data-test-id="ChoiceMultiple_ChoiceFields"
      :fields="fields"
      :selected-field-ids="selectedFieldIds"
      :max-answers="maxAnswers"
      @field-clicked="toggleAnswerChoice"
      @updated-text-choice="(textInput) => updateChoiceTextAnswer(textInput)"
    />
  </ScrollIntersectionObserver>
</template>
<script lang="ts">
import { type DeepReadonly, defineComponent, type PropType } from 'vue'

import type { Answer } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import { type Field, isExclusiveField } from '../../../question/models/Field'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'

import ChoiceFields from './ChoiceFields.vue'

export default defineComponent({
  components: { ChoiceFields, HintText, ScrollIntersectionObserver },
  props: {
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
    fields: { required: true, type: Array<Field> },
    maxAnswers: { required: true, type: Number },
    selectedFieldIds: { required: true, type: Set<string> },
  },
  emits: {
    answersUpdated: (_newAnswers: Answer[]) => true,
  },
  setup(props, { emit }) {
    // Multiple choice normally leaves every other selection untouched when a field is toggled
    // (unlike single choice, which always clears the rest). An exclusive field is the one
    // exception: it behaves like a data-driven "none of the above" for whichever choices the
    // survey maker flagged, so selecting it must clear the rest, and selecting anything else
    // must clear it.
    const isFieldExclusive = (fieldId: string): boolean => {
      const field = props.fields.find((candidate) => candidate.id === fieldId)
      return field !== undefined && isExclusiveField(field)
    }

    const unselectAnswer = (answer: Answer): Answer => {
      switch (answer.type) {
        case 'choice': {
          return { ...answer, value: { selected: false } }
        }
        case 'choiceText': {
          return { ...answer, value: { ...answer.value, selected: false } }
        }
        default: {
          return answer
        }
      }
    }

    const toggleAnswerChoice = (fieldToggled: string): void => {
      const targetAnswer = props.answers.find((answer) => answer.fieldId === fieldToggled)
      const isBeingSelected = targetAnswer?.type === 'choice' && !targetAnswer.value.selected
      const clearsOtherFields = isBeingSelected && isFieldExclusive(fieldToggled)
      const clearsExclusiveFields = isBeingSelected && !isFieldExclusive(fieldToggled)

      emit(
        'answersUpdated',
        props.answers.map((answer) => {
          if (answer.fieldId !== fieldToggled) {
            if (clearsOtherFields) {
              return unselectAnswer(answer)
            }
            if (clearsExclusiveFields && isFieldExclusive(answer.fieldId)) {
              return unselectAnswer(answer)
            }
            return answer
          }
          if (answer.type !== 'choice') {
            throw new TypeError("Expected answer of type 'choice' or 'choiceText'")
          }
          return {
            ...answer,
            value: {
              selected: !answer.value.selected,
            },
          }
        }),
      )
    }
    const updateChoiceTextAnswer = (textInput: string): void => {
      const isBeingSelected = textInput.length > 0
      emit(
        'answersUpdated',
        props.answers.map((answer) => {
          if (answer.fieldId === 'other' && answer.type === 'choiceText') {
            return { ...answer, value: { selected: isBeingSelected, textInput } }
          }
          if (isBeingSelected && isFieldExclusive(answer.fieldId)) {
            return unselectAnswer(answer)
          }
          return answer
        }),
      )
    }

    return {
      toggleAnswerChoice,
      updateChoiceTextAnswer,
      c,
    }
  },
})
</script>
